import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, X, Sparkles, CalendarDays, Quote } from "lucide-react";
import { format } from "date-fns";
import { ro } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, formatApiError } from "@/lib/api";
import { runImageJob } from "@/lib/imageJobs";
import { tagStyle } from "@/lib/tags";
import RichTextEditor from "@/components/RichTextEditor";
import { applyInternalLink } from "@/components/InternalLinkSuggestions";
import SaveLinksDialog from "@/components/SaveLinksDialog";
import ImageUploader from "@/components/ImageUploader";

const THEMES = [
  { key: "pink", label: "Roșu aprins", swatch: "#E81B33" },
  { key: "mint", label: "Alb lăptos", swatch: "#F7F1E8" },
  { key: "lavender", label: "Burgund", swatch: "#9C1F36" },
  { key: "yellow", label: "Aramiu", swatch: "#CF7A2E" },
];

const CATEGORIES = [
  "Psihologie & oameni",
  "Viața de adult",
  "Relații",
  "Internet, social media & cultură",
  "Bani, muncă & carieră",
  "Lifestyle & viața de zi cu zi",
  "Cărți, filme & cultură pop",
];

const toLocalInputValue = (iso) => {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const AdminPostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [coverAlt, setCoverAlt] = useState("");
  const [punchline, setPunchline] = useState("");
  const [punching, setPunching] = useState(false);
  const [colorTheme, setColorTheme] = useState("pink");
  const [published, setPublished] = useState(true);
  const [scheduledFor, setScheduledFor] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [describingAlt, setDescribingAlt] = useState(false);
  const [pendingLinks, setPendingLinks] = useState(null);
  const linksChecked = useRef(false);
  const [bodyImages, setBodyImages] = useState([
    { url: "", alt: "" },
    { url: "", alt: "" },
    { url: "", alt: "" },
  ]);

  const suggestAlt = async (url) => {
    if (!url) return;
    setDescribingAlt(true);
    try {
      const { data } = await api.post("/admin/describe-image", { url });
      if (data?.alt) {
        setCoverAlt(data.alt);
        toast.success("AI-ul a propus o descriere pentru imagine — o poți edita.");
      }
    } catch {
      // fără propunere AI — Alexia poate scrie manual
    } finally {
      setDescribingAlt(false);
    }
  };

  const setBodyImage = (i, patch) =>
    setBodyImages((prev) => prev.map((img, idx) => (idx === i ? { ...img, ...patch } : img)));

  const suggestBodyAlt = async (i, url) => {
    if (!url) return;
    setBodyImage(i, { describing: true });
    try {
      const { data } = await api.post("/admin/describe-image", { url });
      if (data?.alt) setBodyImage(i, { alt: data.alt });
    } catch {
      // fără propunere AI
    } finally {
      setBodyImage(i, { describing: false });
    }
  };

  const [bodyPrompts, setBodyPrompts] = useState(["", "", ""]);
  const [bodyPresence, setBodyPresence] = useState(["auto", "auto", "auto"]);
  const [bodyGenerating, setBodyGenerating] = useState(null);
  const generateBodyImage = async (i) => {
    const prompt = bodyPrompts[i].trim();
    if (prompt.length < 5) {
      toast.error("Descrie în câteva cuvinte imaginea dorită.");
      return;
    }
    setBodyGenerating(i);
    try {
      const data = await runImageJob("body", { prompt, title: title.trim(), alexia: bodyPresence[i] });
      setBodyImage(i, { url: data.url, alt: data.alt || "" });
      toast.success(`Poza ${i + 1} a fost generată!`);
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut genera poza"));
    } finally {
      setBodyGenerating(null);
    }
  };

  const handleBodyImageChange = (i, url) => {
    setBodyImage(i, { url, ...(url ? {} : { alt: "" }) });
    if (url) suggestBodyAlt(i, url);
  };

  const handleCoverChange = (url) => {
    setCoverImage(url);
    suggestAlt(url);
  };

  const generateCover = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Descrie în câteva cuvinte imaginea dorită.");
      return;
    }
    setGenerating(true);
    try {
      const data = await runImageJob("cover", { prompt: aiPrompt.trim() });
      setCoverImage(data.url);
      toast.success("Coperta a fost generată!");
      suggestAlt(data.url);
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut genera coperta"));
    } finally {
      setGenerating(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim().replace(/[,\n\t]/g, "").slice(0, 30);
    if (!t) return;
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setTagInput("");
      return;
    }
    if (tags.length >= 6) {
      toast.error("Maxim 6 etichete pe însemnare.");
      return;
    }
    setTags((prev) => [...prev, t]);
    setTagInput("");
  };

  const removeTag = (t) => setTags((prev) => prev.filter((x) => x !== t));

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/admin/posts/${id}`)
      .then((r) => {
        const p = r.data;
        setTitle(p.title);
        setExcerpt(p.excerpt || "");
        setContent(p.content || "");
        setCoverImage(p.cover_image || "");
        setCoverAlt(p.cover_alt || "");
        setPunchline(p.punchline || "");
        setColorTheme(p.color_theme || "pink");
        setPublished(p.published);
        setTags(p.tags || []);
        setCategory(p.category || "");
        setScheduledFor(p.scheduled_for ? toLocalInputValue(p.scheduled_for) : "");
        const imgs = (p.body_images || []).slice(0, 3);
        setBodyImages([0, 1, 2].map((i) => ({ url: imgs[i]?.url || "", alt: imgs[i]?.alt || "" })));
      })
      .catch((e) => toast.error(formatApiError(e, "Nu am putut încărca însemnarea")))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const buildPayload = (finalContent) => ({
    title: title.trim(),
    excerpt: excerpt.trim(),
    content: finalContent,
    cover_image: coverImage.trim() || null,
    cover_alt: coverAlt.trim(),
    punchline: punchline.trim(),
    color_theme: colorTheme,
    published: scheduledFor ? false : published,
    tags,
    category: category || null,
    scheduled_for: scheduledFor ? new Date(scheduledFor).toISOString() : "",
    body_images: bodyImages.filter((i) => i.url).map((i) => ({ url: i.url, alt: (i.alt || "").trim() })),
    clear_body_images: isEdit && bodyImages.every((i) => !i.url),
  });

  const submit = async (finalContent) => {
    setPendingLinks(null);
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/posts/${id}`, buildPayload(finalContent));
        toast.success("Însemnare salvată");
      } else {
        await api.post("/admin/posts", buildPayload(finalContent));
        toast.success("Însemnare publicată");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut salva"));
    } finally {
      setSaving(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim() || content.trim() === "<br>") {
      toast.error("Un titlu și puțin conținut, te rog.");
      return;
    }
    if (scheduledFor && new Date(scheduledFor) <= new Date()) {
      toast.error("Alege un moment din viitor pentru programare.");
      return;
    }
    const plainLen = content.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().length;
    const hasInternalLink = /<a\s[^>]*href=["']\/post\//i.test(content);
    if (!hasInternalLink && plainLen >= 200 && !linksChecked.current) {
      linksChecked.current = true;
      setSaving(true);
      try {
        const { data } = await api.post("/admin/editor-links", { content_html: content, title, exclude_id: id || null });
        if (data?.suggestions?.length) {
          setSaving(false);
          setPendingLinks(data.suggestions);
          return;
        }
      } catch {
        // dacă asistentul nu răspunde, salvăm liniștit fără propuneri
      }
    }
    await submit(content);
  };

  const confirmLinks = (chosen) => {
    const holder = document.createElement("div");
    holder.innerHTML = content;
    chosen.forEach((s) => applyInternalLink(holder, s));
    setContent(holder.innerHTML);
    toast.success(`Am inserat ${chosen.length} ${chosen.length === 1 ? "legătură" : "legături"}.`);
    submit(holder.innerHTML);
  };

  if (loading) {
    return <p className="py-24 text-center text-sm text-[#F7F1E8]/68" data-testid="editor-loading">Se încarcă…</p>;
  }

  return (
    <main data-testid="admin-editor" className="mx-auto max-w-3xl px-6 py-12">
      <Link
        to="/admin"
        data-testid="editor-back-link"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
      </Link>
      <h1 className="mt-6 font-serif text-4xl font-light text-[#F7F1E8]">
        {isEdit ? "Editează însemnarea" : "O însemnare nouă"}
      </h1>

      <form onSubmit={save} className="mt-10 space-y-6">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Titlu</label>
          <input
            data-testid="editor-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-2 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-3 font-serif text-2xl focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
            required
          />
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Replica de final (apare cu roșu, la capătul textului)</label>
            <button
              type="button"
              data-testid="editor-punchline-ai-btn"
              disabled={punching}
              onClick={async () => {
                const plain = content.replace(/<[^>]+>/g, " ").trim();
                if (plain.length < 40) { toast.error("Scrie întâi textul însemnării — replica se trage din el."); return; }
                setPunching(true);
                try {
                  const { data } = await api.post("/admin/punchline", { title, content_html: content });
                  setPunchline(data.punchline);
                  toast.success("AI-ul a propus o replică — o poți edita.");
                } catch (e) {
                  toast.error(formatApiError(e, "Nu am putut genera replica"));
                } finally {
                  setPunching(false);
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--accent)_40%,transparent)] px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-[var(--accent-dark)] transition-colors hover:bg-[var(--tint-pink)] disabled:opacity-50"
            >
              <Quote className="h-3 w-3" strokeWidth={1.8} /> {punching ? "Se scrie…" : "Propune cu AI"}
            </button>
          </div>
          <input
            data-testid="editor-punchline-input"
            value={punchline}
            onChange={(e) => setPunchline(e.target.value)}
            maxLength={200}
            placeholder="Lasă gol și o scrie AI-ul la salvare — sau scrie-o tu, scurt și tăios."
            className="mt-2 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-3 font-serif text-base italic text-[var(--accent-dark)] focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Rezumat scurt</label>
          <textarea
            data-testid="editor-excerpt-input"
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            rows={2}
            maxLength={400}
            className="mt-2 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-3 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
          />
        </div>

        <div>
          <ImageUploader
            testId="editor-cover-upload"
            label="Imagine copertă"
            aspect="aspect-[16/9]"
            value={coverImage}
            onChange={handleCoverChange}
          />
          {coverImage && (
            <div className="mt-3">
              <div className="flex items-center justify-between gap-2">
                <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">
                  Descrierea imaginii (text alternativ)
                </label>
                <button
                  type="button"
                  data-testid="cover-alt-ai-btn"
                  onClick={() => suggestAlt(coverImage)}
                  disabled={describingAlt}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--accent)_30%,transparent)] px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-[var(--accent)] transition-colors hover:bg-[color-mix(in_srgb,var(--tint-pink)_50%,transparent)] disabled:opacity-50"
                >
                  <Sparkles className="h-3 w-3" strokeWidth={1.6} />
                  {describingAlt ? "AI-ul scrie…" : "Propune cu AI"}
                </button>
              </div>
              <input
                data-testid="editor-cover-alt-input"
                value={coverAlt}
                onChange={(e) => setCoverAlt(e.target.value)}
                maxLength={250}
                placeholder={describingAlt ? "AI-ul privește imaginea…" : "ex: o cană de cafea aburindă lângă un caiet cu planificări"}
                className="mt-1.5 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-2.5 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
              />
              <p className="mt-1 text-[11px] text-[#F7F1E8]/68">
                Ajută SEO și cititorii cu deficiențe de vedere. Descrie simplu ce se vede în poză.
              </p>
            </div>
          )}
          <div className="mt-3 rounded-xl border border-dashed border-[color-mix(in_srgb,var(--accent)_30%,transparent)] bg-[color-mix(in_srgb,var(--tint-pink)_40%,transparent)] p-4">
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[var(--accent)]">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={1.6} /> Sau generează coperta cu AI
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                data-testid="ai-cover-prompt-input"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!generating) generateCover();
                  }
                }}
                placeholder="ex: eu în fața oglinzii, cu telefonul întors cu fața în jos"
                maxLength={500}
                className="w-full flex-1 rounded-full border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-2.5 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
              />
              <button
                type="button"
                data-testid="ai-cover-generate-btn"
                onClick={generateCover}
                disabled={generating}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--accent)] px-5 py-2.5 text-xs font-medium uppercase tracking-[0.15em] text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-60"
              >
                <Sparkles className="h-3.5 w-3.5" strokeWidth={1.6} />
                {generating ? "Se generează…" : "Generează"}
              </button>
            </div>
            {generating && (
              <p className="mt-2 text-[11px] text-[#F7F1E8]/68" data-testid="ai-cover-generating-note">
                Pictez coperta… durează 15–30 de secunde.
              </p>
            )}
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Culoarea cardului</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {THEMES.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`editor-theme-${t.key}`}
                onClick={() => setColorTheme(t.key)}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-all ${
                  colorTheme === t.key
                    ? "border-[#F7F1E8] text-[#F7F1E8]"
                    : "border-[color-mix(in_srgb,var(--milk)_18%,transparent)] text-[#F7F1E8]/78 hover:border-[color-mix(in_srgb,var(--milk)_20%,transparent)]"
                }`}
              >
                <span className="h-3 w-3 rounded-full" style={{ background: t.swatch }} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Categorie</p>
          <select
            data-testid="editor-category-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="mt-2 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-2.5 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
          >
            <option value="">Alege automat (AI decide după conținut)</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <p className="mt-1.5 text-[11px] text-[#F7F1E8]/68">
            Dacă nu alegi nimic, însemnarea intră singură în categoria potrivită.
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Etichete</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-3 py-2.5 focus-within:border-[var(--accent)] focus-within:ring-2 focus-within:ring-[var(--tint-pink)]">
            {tags.map((t) => (
              <span
                key={t}
                data-testid={`editor-tag-${t}`}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${tagStyle(t)}`}
              >
                {t}
                <button
                  type="button"
                  data-testid={`editor-tag-remove-${t}`}
                  onClick={() => removeTag(t)}
                  className="ml-0.5 rounded-full opacity-60 hover:opacity-100"
                  aria-label={`Șterge eticheta ${t}`}
                >
                  <X className="h-3 w-3" strokeWidth={2} />
                </button>
              </span>
            ))}
            <input
              data-testid="editor-tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  addTag();
                }
              }}
              onBlur={addTag}
              placeholder={tags.length ? "" : "ex: recreație, lecturi, dimineți…"}
              className="min-w-[140px] flex-1 border-none bg-transparent py-0.5 text-sm outline-none placeholder:text-[#F7F1E8]/58"
              maxLength={30}
            />
          </div>
          <p className="mt-2 text-[11px] text-[#F7F1E8]/68">
            Apasă Enter sau virgulă după fiecare etichetă. Maxim 6 — cititorii pot filtra după ele pe prima pagină.
          </p>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Povestea</label>
          <div className="mt-2">
            <RichTextEditor value={content} onChange={setContent} title={title} excludeId={id || null} placeholder="A fost odată o zi de marți…" />
          </div>
        </div>

        <div className="rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] p-4">
          <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">
            Pozele din text (până la 3)
          </label>
          <p className="mt-1 text-[11px] text-[#F7F1E8]/68">
            Se așază singure printre paragrafe — una spre început, una la mijloc, una spre final — iar textul le încadrează frumos.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {bodyImages.map((img, i) => (
              <div key={i}>
                <ImageUploader
                  testId={`editor-body-image-${i + 1}`}
                  label={`Poza ${i + 1}`}
                  aspect="aspect-[4/3]"
                  value={img.url}
                  onChange={(url) => handleBodyImageChange(i, url)}
                  allowRemove
                />
                <div className="mt-2 rounded-xl border border-dashed border-[color-mix(in_srgb,var(--accent)_30%,transparent)] bg-[color-mix(in_srgb,var(--tint-pink)_40%,transparent)] p-3" data-testid={`editor-body-ai-${i + 1}`}>
                  <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]">
                    <Sparkles className="h-3 w-3" strokeWidth={1.6} /> Sau generează poza cu AI
                  </p>
                  <input
                    data-testid={`editor-body-ai-prompt-${i + 1}`}
                    value={bodyPrompts[i]}
                    onChange={(e) => setBodyPrompts((p) => p.map((v, idx) => (idx === i ? e.target.value : v)))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (bodyGenerating === null) generateBodyImage(i);
                      }
                    }}
                    maxLength={1500}
                    placeholder="ex: eu la masă, cu o scrisoare nedeschisă"
                    className="mt-2 w-full rounded-full border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-3 py-2 text-xs focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <select
                      data-testid={`editor-body-ai-presence-${i + 1}`}
                      value={bodyPresence[i]}
                      onChange={(e) => setBodyPresence((p) => p.map((v, idx) => (idx === i ? e.target.value : v)))}
                      className="rounded-full border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-2 py-1.5 text-[10px] uppercase tracking-[0.1em] text-[#F7F1E8]"
                    >
                      <option value="auto">Lasă AI-ul</option>
                      <option value="yes">Cu mine</option>
                      <option value="no">Fără mine</option>
                    </select>
                    <button
                      type="button"
                      data-testid={`editor-body-ai-generate-${i + 1}`}
                      onClick={() => generateBodyImage(i)}
                      disabled={bodyGenerating !== null}
                      className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[var(--accent)] px-3.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.15em] text-white disabled:opacity-60"
                    >
                      <Sparkles className="h-3 w-3" strokeWidth={1.6} /> {bodyGenerating === i ? "Se generează…" : "Generează"}
                    </button>
                  </div>
                </div>
                {img.url && (
                  <input
                    data-testid={`editor-body-alt-${i + 1}`}
                    value={img.alt}
                    onChange={(e) => setBodyImage(i, { alt: e.target.value })}
                    maxLength={250}
                    placeholder={img.describing ? "AI-ul privește imaginea…" : "Descrierea pozei (o propune AI-ul)"}
                    className="mt-2 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-3 py-2 text-xs focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3 rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] p-4">
          <label className="flex items-center gap-3 text-sm text-[#F7F1E8]/86">
            <input
              type="checkbox"
              data-testid="editor-published-toggle"
              checked={!scheduledFor && published}
              disabled={Boolean(scheduledFor)}
              onChange={(e) => setPublished(e.target.checked)}
              className="h-4 w-4 rounded border-[color-mix(in_srgb,var(--milk)_20%,transparent)] accent-[var(--accent)] disabled:opacity-40"
            />
            Publică imediat
          </label>
          <div className="border-t border-[color-mix(in_srgb,var(--milk)_18%,transparent)] pt-3">
            <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">
              Sau programează publicarea
            </label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="editor-schedule-input"
                    className={`inline-flex items-center gap-2 rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-2.5 text-sm transition-colors hover:border-[var(--accent)] ${scheduledFor ? "text-[#F7F1E8]" : "text-[#F7F1E8]/68"}`}
                  >
                    <CalendarDays className="h-4 w-4 text-[var(--accent)]" strokeWidth={1.6} />
                    {scheduledFor
                      ? format(new Date(scheduledFor), "d MMMM yyyy", { locale: ro })
                      : "Alege ziua"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto rounded-2xl border-[color-mix(in_srgb,var(--milk)_15%,transparent)] p-2" align="start">
                  <Calendar
                    mode="single"
                    locale={ro}
                    selected={scheduledFor ? new Date(scheduledFor) : undefined}
                    disabled={{ before: new Date() }}
                    onSelect={(day) => {
                      if (!day) return;
                      const time = scheduledFor ? scheduledFor.slice(11, 16) : "08:00";
                      const pad = (n) => String(n).padStart(2, "0");
                      setScheduledFor(`${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}T${time}`);
                    }}
                  />
                </PopoverContent>
              </Popover>
              {scheduledFor && (
                <input
                  type="time"
                  data-testid="editor-schedule-time"
                  value={scheduledFor.slice(11, 16)}
                  onChange={(e) => setScheduledFor(`${scheduledFor.slice(0, 10)}T${e.target.value || "08:00"}`)}
                  className="rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-3 py-2.5 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
                />
              )}
              {scheduledFor && (
                <button
                  type="button"
                  data-testid="editor-schedule-clear-btn"
                  onClick={() => setScheduledFor("")}
                  className="text-xs uppercase tracking-[0.15em] text-[var(--accent)] underline underline-offset-4"
                >
                  Anulează programarea
                </button>
              )}
            </div>
            <p className="mt-2 text-[11px] text-[#F7F1E8]/68">
              {scheduledFor
                ? "Însemnarea va apărea pe blog automat la data aleasă, iar abonații vor fi anunțați atunci."
                : "Dacă alegi o dată, însemnarea rămâne ascunsă și se publică singură la momentul respectiv."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            data-testid="editor-save-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-60"
          >
            <Save className="h-3.5 w-3.5" strokeWidth={1.6} />
            {saving ? "Se salvează…" : isEdit ? "Salvează modificările" : scheduledFor ? "Programează însemnarea" : published ? "Publică însemnarea" : "Salvează ca ciornă"}
          </button>
          <Link
            to="/admin"
            data-testid="editor-cancel-link"
            className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/68 hover:text-[#F7F1E8]"
          >
            Anulează
          </Link>
        </div>
      </form>
      {pendingLinks && <SaveLinksDialog suggestions={pendingLinks} onConfirm={confirmLinks} onSkip={() => submit(content)} />}
    </main>
  );
};

export default AdminPostEditor;
